import { useCallback } from 'react';
import IconButton from '@mui/material/IconButton';
import { Grid2 as Grid, Stack, Typography } from '@mui/material';
import { PlusIcon, Tooltip } from 'framework/ui/mui-builder';
import { FormattedTextField, getAnchorRowStyle } from 'framework/ui/field';
import { EmployeeLinkFieldWithNameLoading } from 'domain/shared/employee';
import { DATE_FORMAT, openPageInNewTab, useDateService } from 'framework/utils';
import moment from 'moment';
import StatusField from '../../changeRequests/List/fields/StatusField';
import { UiThemeOverrider } from 'framework/uiTheme';
import {
  StatusFilter,
  WASTE_STATUS_OPTIONS,
  REPORT_WASTE,
  useNewProjectUrl,
  ProjectSectionAccordion,
} from 'domain/shared/newProject';
import RedmineLinkField from './fields/RedmineLinksField';
import { useAuthorization } from 'framework/authorization';
import { useLocation, useParams } from 'react-router-dom';
import UpdateWasteField from './fields/UpdateWasteField';
import PreviewWastePageLinkField from './fields/PreviewWastePageLinkField';
import { defaultsDeep } from 'lodash';
import {
  ListFiltersLayout,
  DropDownFilterInputLayout,
  EditableListDataGridLayout,
  EDITABLE_LIST_CUSTOM,
  EDITABLE_LIST_FIELD,
} from 'framework/list';

const uniqueStatusLabels = [...new Set(Object.values(WASTE_STATUS_OPTIONS).filter(label => label))];
const WASTE_STATUS_FILTER_OPTIONS: Dictionary<string> = Object.fromEntries(
  uniqueStatusLabels.map(label => [label, label]),
);

const WasteList = () => {
  const { datesDifference, getToday } = useDateService();
  const { getCreateWasteUrl } = useNewProjectUrl();
  const { id } = useParams();
  const { hash } = useLocation();
  const { isGranted } = useAuthorization();

  const filters = (
    <UiThemeOverrider
      overriddenLayouts={{
        DropdownLayout: DropDownFilterInputLayout,
      }}
    >
      <ListFiltersLayout>
        <StatusFilter source="statusLabel" options={WASTE_STATUS_FILTER_OPTIONS} />
      </ListFiltersLayout>
    </UiThemeOverrider>
  );

  const renderActions = useCallback(() => {
    if (!isGranted(REPORT_WASTE)) {
      return [];
    }
    return [
      <IconButton sx={{ p: 0, ml: 1 }} key="add" onClick={() => openPageInNewTab(getCreateWasteUrl(id!))}>
        <PlusIcon />
      </IconButton>,
    ];
  }, [getCreateWasteUrl, id, isGranted]);

  return (
    <ProjectSectionAccordion
      defaultExpanded
      title={
        <Grid
          container
          direction="row"
          sx={{ justifyContent: 'space-between', alignItems: 'center', width: '100%', pl: 2 }}
        >
          <Grid size={3}>
            <Typography variant="h4" color="primary">
              Waste
            </Typography>
          </Grid>
          <Grid size="auto" alignItems="center" sx={{ '.filter-item-wrapper': { m: 0 } }}>
            {filters}
          </Grid>
          <Grid size="grow" />
          <Grid size="auto">
            <Stack spacing={1} alignItems="center" justifyContent="space-between" direction="row">
              {renderActions()}
            </Stack>
          </Grid>
        </Grid>
      }
    >
      <EditableListDataGridLayout
        rowProps={data =>
          defaultsDeep(getAnchorRowStyle(data, hash), {
            sx: {
              padding: 0,
              px: 1,
              alignItems: 'center',
              height: '56px',
            },
          })
        }
        getTableCells={isEditModeLayout =>
          Promise.resolve([
            {
              type: EDITABLE_LIST_FIELD,
              source: 'alias',
              isEditor: false,
              component: PreviewWastePageLinkField,
              componentProps: { label: '', idSource: 'id', id },
              gridCellProps: { size: 0.5 },
            },
            {
              type: EDITABLE_LIST_FIELD,
              source: 'reason',
              isEditor: false,
              component: FormattedTextField,
              componentProps: {
                label: '',
                format: (reason: string) => {
                  return reason.length > 60 ? (
                    <Tooltip title={reason}>
                      <span>
                        <Typography noWrap>{reason}</Typography>
                      </span>
                    </Tooltip>
                  ) : (
                    reason
                  );
                },
              },
              gridCellProps: { size: 3, sx: { '.MuiFormControl-root': { mb: 0 } } },
            },
            {
              type: EDITABLE_LIST_FIELD,
              source: 'createdAt',
              isEditor: false,
              component: FormattedTextField,
              componentProps: {
                label: '',
                format: (str: string) => (
                  <Tooltip title={moment(str).format(DATE_FORMAT.API_ONLY_DATE)}>
                    <span>{datesDifference(getToday(), str)}</span>
                  </Tooltip>
                ),
              },
              gridCellProps: { size: 1.5, sx: { '.MuiFormControl-root': { mb: 0 } } },
            },
            {
              type: EDITABLE_LIST_FIELD,
              source: 'laborWasteDetails.employeeAlias',
              isEditor: false,
              component: EmployeeLinkFieldWithNameLoading,
              componentProps: { label: '' },
              gridCellProps: { size: 2.5, sx: { '.MuiFormControl-root': { mb: 0 } } },
            },
            {
              type: EDITABLE_LIST_FIELD,
              source: 'laborWasteDetails.ticketsIds',
              isEditor: false,
              component: RedmineLinkField,
              componentProps: { label: '' },
              gridCellProps: { size: 2, sx: { '.MuiFormControl-root': { mb: 0 } } },
            },
            {
              type: EDITABLE_LIST_FIELD,
              source: 'status',
              isEditor: false,
              component: StatusField,
              componentProps: { label: '' },
              gridCellProps: {
                size: 1,
                sx: { '.MuiFormControl-root': { mb: 0 } },
              },
            },
            {
              type: EDITABLE_LIST_CUSTOM,
              isEditor: false,
              component: UpdateWasteField,
              componentProps: {
                statusSource: 'status',
                idSource: 'id',
                afsArtifactId: 'afsArtifactId',
                id,
              },
              gridCellProps: {
                size: 1,
                sx: {
                  '.MuiFormControl-root': { mb: 0 },
                  display: 'flex',
                  justifyContent: 'end',
                },
              },
            },
          ])
        }
      />
    </ProjectSectionAccordion>
  );
};
export default WasteList;
